import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProducts,
  deleteProduct,
  addStockToWarehouse,
} from "../../store/slices/productSlice";
import { RootDispatch, RootState } from "../../store";
import ProductModal from "./productModal";
import Loading from "../../components/AssetsComponent/loading";
import Button, { ButtonType } from "../../components/Assets/Button";
import { fetchDropdownData } from "../../store/slices/storeInwardSlice";
import { IProduct } from "../../models/productData.model";
import toast from "react-hot-toast";
import Select from "react-select";
import CreateProductBOM from "./productOrMaterialBOMModal";
import StockReductionHistoryDrawer from "../Warehouse/Management/StockReductionDrawer";
import {
  CirclePlus,
  ClockArrowDown,
  Pencil,
  ReceiptText,
  Trash2,
} from "lucide-react";

const ProductManagement: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();

  const products = useSelector((state: RootState) => state.product.products);
  const loading = useSelector((state: RootState) => state.product.loading);

  const { role, userName } = useSelector((state: RootState) => state.auth);

  const warehouses = useSelector(
    (state: RootState) => state.storeInward.warehouses,
  );

  const warehouseAssigned = userName?.warehouseAssigned;

  const isPrivileged =
    role === "admin" ||
    role === "factory" ||
    role === "bdm" ||
    role === "dispatch-lv1";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [showAddStock, setShowAddStock] = useState(false);

  const [stockQuantity, setStockQuantity] = useState<number>(0);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");

  const [addingStock, setAddingStock] = useState(false);

  const [productPrice, setProductPrice] = useState<number>(0);
  const [gst, setGst] = useState<number>(0);

  const [transportPrice, setTransportPrice] = useState<number>(0);

  const [handlingCost, setHandlingCost] = useState<number>(0);

  const [isProductBomModalOpen, setIsProductBomModalOpen] = useState(false);
  const [selectedProductBom, setSelectedProductBom] = useState<string | null>(
    null,
  );
  const [selectedProductId, setSelectedProductId] = useState<
    string | undefined
  >();
  const [isStockReductionLogsOpen, setIsStockReductionLogsOpen] =
    useState(false);

  const handleStockReductionLogs = (productId: string) => {
    setSelectedProductId(productId);
    setIsStockReductionLogsOpen(true);
  };

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchDropdownData("factory"));
  }, [dispatch]);

  useEffect(() => {
    if (showAddStock && warehouseAssigned && warehouses.length > 0) {
      const defaultWarehouse = warehouses.find(
        (w) => w._id === warehouseAssigned,
      );

      if (defaultWarehouse) {
        setSelectedWarehouse(defaultWarehouse._id);
      }
    }
  }, [showAddStock, warehouseAssigned, warehouses]);

  const filteredProducts = Array.isArray(products)
    ? products.filter((product) =>
        product?.productName?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : [];

  const sanitizeValue = (value: number | string) => {
    if (typeof value === "number" && value < 0) return 0;
    return value;
  };

  const getQuantityColor = (quantity: number) => {
    if (quantity > 50) {
      return "text-green-700 bg-green-50 border-green-200";
    } else if (quantity <= 50 && quantity >= 15) {
      return "text-yellow-700 bg-yellow-50 border-yellow-200";
    } else {
      return "text-red-700 bg-red-50 border-red-200";
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct?._id) return;

    try {
      await dispatch(deleteProduct(selectedProduct._id)).unwrap();

      toast.success("Product deleted successfully");

      setIsDeleteModalOpen(false);
      setSelectedProduct(null);
    } catch (err) {
      toast.error("Failed to delete product");
    }
  };

  const handleAddStock = async () => {
    if (!stockQuantity || !selectedWarehouse || !selectedProduct) return;

    setAddingStock(true);

    try {
      await dispatch(
        addStockToWarehouse({
          productId: selectedProduct._id,
          warehouseId: selectedWarehouse,
          quantity: stockQuantity,
          productPrice,
          gst,
          transportPrice,
          handlingCost,
        }),
      ).unwrap();

      toast.success("Stock added successfully");

      setShowAddStock(false);

      setStockQuantity(0);
      setSelectedWarehouse("");
      setProductPrice(0);
      setGst(0);
      setTransportPrice(0);
      setHandlingCost(0);
    } catch (err) {
      toast.error("Failed to add stock");
    } finally {
      setAddingStock(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      {/* Header */}
      {/* Search + Add */}
      <div className="flex flex-col md:flex-row justify-start gap-4 items-center mb-5 w-full">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2.5 border border-gray-300 rounded-lg w-full md:flex-1"
        />

        {isPrivileged && (
          <Button
            type={ButtonType.Submit}
            onClick={() => {
              setSelectedProduct(null);
              setIsEditMode(false);
              setIsModalOpen(true);
            }}
            className="w-full md:w-auto"
          >
            Add Product
          </Button>
        )}
      </div>

      {/* Table Wrapper */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden w-full">
        {/* Desktop */}
        <div className="hidden xl:block w-full overflow-x-auto scrollbar-thin">
          <div className="min-w-[1100px]">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Product
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Stock
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Price
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Created By
                  </th>

                  <th className="w-[180px] px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <tr
                      key={product._id}
                      className="hover:bg-gray-50 transition-all"
                    >
                      {/* Product */}
                      <td className="px-4 py-4 align-top w-[260px]">
                        <div className="space-y-2">
                          <p className="font-semibold text-gray-900 text-sm break-words">
                            {product.productName}
                          </p>

                          <div className="flex flex-wrap gap-2">
                            <span className="text-[11px] px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                              MSP: {sanitizeValue(product.msp)}
                            </span>

                            <span className="text-[11px] px-2 py-1 rounded-full bg-purple-50 text-purple-700">
                              HSN: {sanitizeValue(product.hsn)}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Stock */}
                      <td className="px-4 py-4 align-top w-[320px]">
                        <div className="flex flex-wrap gap-2">
                          {product.warehouseStock &&
                          product.warehouseStock.length > 0 ? (
                            product.warehouseStock.map((ws, i) => (
                              <div
                                key={i}
                                className={`text-[11px] px-2.5 py-1 rounded-full border ${getQuantityColor(
                                  ws.quantity,
                                )}`}
                              >
                                <span className="font-medium">
                                  {ws.warehouse.name}
                                </span>

                                <span className="mx-1">•</span>

                                <span>{sanitizeValue(ws.quantity)}</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-600 border border-red-100">
                              No stock
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Price */}
                      <td className="px-4 py-4 align-top w-[180px]">
                        <div className="space-y-2 text-sm">
                          <p>
                            <span className="font-semibold text-gray-700">
                              Price:
                            </span>{" "}
                            {sanitizeValue(product.price)}
                          </p>

                          <p>
                            <span className="font-semibold text-gray-700">
                              GST:
                            </span>{" "}
                            {sanitizeValue(Number(product.gst))}%
                          </p>
                        </div>
                      </td>

                      {/* Created By */}
                      <td className="px-4 py-4 align-top w-[16px]">
                        <p className="text-sm text-gray-700 break-words">
                          {product.createdBy?.username || "-"}
                        </p>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4 align-top min-w-[240px]">
                        <div className="flex items-center justify-end gap-2 flex-nowrap pr-2">
                          <div className="relative group flex-shrink-0">
                            <button
                              className="h-9 w-9 rounded-lg bg-orange-50 hover:bg-orange-100 text-orange-700 transition-all flex items-center justify-center"
                              onClick={() =>
                                handleStockReductionLogs(product._id)
                              }
                            >
                              <ClockArrowDown size={15} />
                            </button>

                            <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                              View Stock Reduction logs
                            </div>
                          </div>
                          {/* Add Stock */}
                          {isPrivileged && (
                            <div className="relative group flex-shrink-0">
                              <button
                                className="h-9 w-9 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 transition-all flex items-center justify-center"
                                onClick={() => {
                                  setSelectedProduct(product);

                                  setShowAddStock(true);
                                }}
                              >
                                <CirclePlus size={15} />
                              </button>

                              <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                Add Stock
                              </div>
                            </div>
                          )}

                          {/* BOM */}
                          {(isPrivileged || role === "store") && (
                            <div className="relative group flex-shrink-0">
                              <button
                                className="h-9 w-9 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-all flex items-center justify-center"
                                onClick={() => {
                                  //TODO: Implement BOM view
                                  setSelectedProduct(product);

                                  setSelectedProductBom(product?.bomId || null);

                                  setIsProductBomModalOpen(true);
                                }}
                              >
                                <ReceiptText size={15} />
                              </button>

                              <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                BOM
                              </div>
                            </div>
                          )}

                          {/* Edit */}
                          {isPrivileged && (
                            <div className="relative group flex-shrink-0">
                              <button
                                className="h-9 w-9 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 transition-all flex items-center justify-center"
                                onClick={() => {
                                  setSelectedProduct(product);

                                  setIsEditMode(true);

                                  setIsModalOpen(true);
                                }}
                              >
                                <Pencil size={15} />
                              </button>

                              <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                Edit Product
                              </div>
                            </div>
                          )}

                          {/* Delete */}
                          {isPrivileged && (
                            <div className="relative group flex-shrink-0">
                              <button
                                className="h-9 w-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 transition-all flex items-center justify-center"
                                onClick={() => {
                                  setSelectedProduct(product);

                                  setIsDeleteModalOpen(true);
                                }}
                              >
                                <Trash2 size={15} />
                              </button>

                              <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                Delete Product
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-500">
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile */}
        <div className="xl:hidden divide-y divide-gray-200">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product) => (
              <div key={product._id} className="p-4 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm break-words">
                      {product.productName}
                    </h3>

                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-[10px] px-2 py-1 rounded-full bg-blue-50 text-blue-700">
                        MSP: {sanitizeValue(product.msp)}
                      </span>

                      <span className="text-[10px] px-2 py-1 rounded-full bg-purple-50 text-purple-700">
                        HSN: {sanitizeValue(product.hsn)}
                      </span>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 flex-nowrap pr-1 flex-shrink-0 min-w-[190px]">
                    <div className="relative group flex-shrink-0">
                      <button
                        className="h-9 w-9 rounded-lg bg-orange-50 hover:bg-orange-100 text-orange-700 transition-all flex items-center justify-center"
                        onClick={() => handleStockReductionLogs(product._id)}
                      >
                        <ClockArrowDown size={15} />
                      </button>

                      <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                        View Stock Reduction logs
                      </div>
                    </div>
                    {/* Add Stock */}
                    {isPrivileged && (
                      <div className="relative group flex-shrink-0">
                        <button
                          className="h-9 w-9 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 transition-all flex items-center justify-center"
                          onClick={() => {
                            setSelectedProduct(product);

                            setShowAddStock(true);
                          }}
                        >
                          <CirclePlus size={15} />
                        </button>

                        <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                          Add Stock
                        </div>
                      </div>
                    )}

                    {/* BOM */}
                    {(isPrivileged || role === "store") && (
                      <div className="relative group flex-shrink-0">
                        <button
                          className="h-9 w-9 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 transition-all flex items-center justify-center"
                          onClick={() => {
                            //TODO: Implement BOM view
                            setSelectedProduct(product);

                            setSelectedProductBom(product?.bomId || null);

                            setIsProductBomModalOpen(true);
                          }}
                        >
                          <ReceiptText size={15} />
                        </button>

                        <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                          BOM
                        </div>
                      </div>
                    )}

                    {/* Edit */}
                    {isPrivileged && (
                      <div className="relative group flex-shrink-0">
                        <button
                          className="h-9 w-9 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 transition-all flex items-center justify-center"
                          onClick={() => {
                            setSelectedProduct(product);

                            setIsEditMode(true);

                            setIsModalOpen(true);
                          }}
                        >
                          <Pencil size={15} />
                        </button>

                        <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                          Edit Product
                        </div>
                      </div>
                    )}

                    {/* Delete */}
                    {isPrivileged && (
                      <div className="relative group flex-shrink-0">
                        <button
                          className="h-9 w-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 transition-all flex items-center justify-center"
                          onClick={() => {
                            setSelectedProduct(product);

                            setIsDeleteModalOpen(true);
                          }}
                        >
                          <Trash2 size={15} />
                        </button>

                        <div className="absolute bottom-full mb-2 right-0 whitespace-nowrap bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                          Delete Product
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stock */}
                <div className="flex flex-wrap gap-2">
                  {product.warehouseStock &&
                  product.warehouseStock.length > 0 ? (
                    product.warehouseStock.map((ws, i) => (
                      <div
                        key={i}
                        className={`text-[11px] px-2.5 py-1 rounded-full border ${getQuantityColor(
                          ws.quantity,
                        )}`}
                      >
                        <span className="font-medium">{ws.warehouse.name}</span>

                        <span className="mx-1">•</span>

                        <span>{sanitizeValue(ws.quantity)}</span>
                      </div>
                    ))
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-600 border border-red-100">
                      No stock
                    </span>
                  )}
                </div>

                {/* Price */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Price</p>

                    <p className="font-semibold text-gray-800 text-sm">
                      {sanitizeValue(product.price)}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">GST</p>

                    <p className="font-semibold text-gray-800 text-sm">
                      {sanitizeValue(Number(product.gst))}%
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Created By: {product.createdBy?.username || "-"}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              No products found
            </div>
          )}
        </div>
      </div>

      {/* Product Modal */}
      <ProductModal
        isOpen={isModalOpen}
        isModalOpen={isModalOpen}
        closeModal={() => {
          setIsModalOpen(false);
          setIsEditMode(false);
        }}
        setIsModalOpen={setIsModalOpen}
        product={selectedProduct || undefined}
        isEditMode={isEditMode}
      />

      {/* Delete Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Confirm Delete
            </h2>

            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this product?
            </p>

            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <Button
                type={ButtonType.Cancel}
                onClick={() => setIsDeleteModalOpen(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>

              <button
                onClick={handleDelete}
                className="px-5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all w-full sm:w-auto"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      {showAddStock && selectedProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Add Stock
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  {selectedProduct.productName}
                </p>
              </div>

              <button
                onClick={() => setShowAddStock(false)}
                className="text-gray-400 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Quantity */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Quantity *
                </label>

                <input
                  type="number"
                  min="1"
                  step="1"
                  value={stockQuantity || ""}
                  onChange={(e) =>
                    setStockQuantity(Number(e.target.value) || 0)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Total Product Price *
                </label>

                <input
                  type="number"
                  min="0"
                  value={productPrice || ""}
                  onChange={(e) => setProductPrice(Number(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Warehouse */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Select Warehouse *
                </label>

                <Select
                  options={warehouses.map((w) => ({
                    value: w._id,
                    label: w.name || "",
                  }))}
                  value={
                    selectedWarehouse
                      ? {
                          value: selectedWarehouse,
                          label:
                            warehouses.find((w) => w._id === selectedWarehouse)
                              ?.name || "",
                        }
                      : null
                  }
                  onChange={(opt) => setSelectedWarehouse(opt?.value || "")}
                  placeholder="Select..."
                  isSearchable
                  className="text-sm"
                  classNamePrefix="react-select"
                />
              </div>

              {/* GST */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  GST (%) *
                </label>

                <input
                  type="number"
                  min="0"
                  max="100"
                  value={gst}
                  onChange={(e) => setGst(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Transport */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Transport Price *
                </label>

                <input
                  type="number"
                  min="0"
                  value={transportPrice}
                  onChange={(e) => setTransportPrice(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              {/* Handling */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Handling Cost *
                </label>

                <input
                  type="number"
                  min="0"
                  value={handlingCost}
                  onChange={(e) => setHandlingCost(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8">
              <Button
                type={ButtonType.Cancel}
                onClick={() => setShowAddStock(false)}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>

              <Button
                type={ButtonType.Submit}
                onClick={handleAddStock}
                disabled={!stockQuantity || !selectedWarehouse || addingStock}
                className="w-full sm:w-auto"
              >
                {addingStock ? "Adding..." : "Confirm & Add"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Product BOM Modal */}
      {isProductBomModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[110]">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                Create BOM for {selectedProduct?.productName}
              </h2>
              <button
                onClick={() => {
                  setIsProductBomModalOpen(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <CreateProductBOM
              from="product"
              title={selectedProduct?.productName || "Product"}
              isEdit={!!selectedProductBom}
              selectedBomId={selectedProductBom}
              selectedProductId={selectedProduct?._id}
              onCancel={() => {
                setIsProductBomModalOpen(false);

                setSelectedProductBom(null);
              }}
              onSuccess={() => {
                setIsProductBomModalOpen(false);

                setSelectedProductBom(null);

                dispatch(fetchProducts());
              }}
            />
          </div>
        </div>
      )}
      <StockReductionHistoryDrawer
        open={isStockReductionLogsOpen}
        onClose={() => {
          setIsStockReductionLogsOpen(false);
          setSelectedProductId(undefined);
        }}
        productId={selectedProductId}
      />
    </>
  );
};

export default ProductManagement;
